/**
 * Hook personnalisé pour surveiller les changements d'estimation de jours
 * et proposer des ajustements automatiques du calendrier
 */
import { useState, useEffect, useRef } from 'react';

export const useEstimationWatcher = (form, employees, calculerHeuresBudgetees, calculerCoutEquipe) => {
  const [showAjustementDialog, setShowAjustementDialog] = useState(false);
  const [ajustementData, setAjustementData] = useState(null);
  const previousEstimation = useRef(null);

  useEffect(() => {
    // Ne surveiller que si on a les données nécessaires
    if (!form.chef_equipe_id || !form.dates_chantier || form.dates_chantier.length === 0) {
      return;
    }

    // Calculer l'estimation actuelle
    // 🧱 Phase 4 — Lot E (équipe fixe abolie) : membres = employés réellement assignés au BT (hors chef), plus la fiche.
    const membres = (form.employes_assignes || []).filter(e => e.employee_id && e.employee_id !== form.chef_equipe_id);
    const nbMembres = (form.chef_equipe_id ? 1 : 0) + membres.length;
    
    if (nbMembres === 0) return;

    const equipe = calculerCoutEquipe(form.chef_equipe_id, employees, form.dates_chantier[0]);
    const montant = parseFloat(form.montant_total_facture || form.montant_soumission || 0);
    
    if (montant <= 0 || equipe.coutMoyen <= 0) return;

    const calc = calculerHeuresBudgetees(montant, equipe.coutMoyen, nbMembres, form.dates_chantier);
    
    if (!calc || calc.jours <= 0) return;

    const joursEstimes = parseFloat(calc.jours);
    const joursPlanifies = form.dates_chantier.length;

    // Si c'est la première fois, initialiser
    if (previousEstimation.current === null) {
      previousEstimation.current = {
        jours: joursEstimes,
        nbMembres: nbMembres,
        joursPlanifies: joursPlanifies
      };
      return;
    }

    // Détecter un changement significatif (≥ 0.5 jour)
    const ecartJours = Math.abs(joursEstimes - previousEstimation.current.jours);
    const changementMembres = nbMembres !== previousEstimation.current.nbMembres;

    if (changementMembres && ecartJours >= 0.5) {
      // Proposer un ajustement
      setAjustementData({
        avant: {
          jours: previousEstimation.current.jours,
          nbMembres: previousEstimation.current.nbMembres,
          joursPlanifies: previousEstimation.current.joursPlanifies
        },
        apres: {
          jours: joursEstimes,
          nbMembres: nbMembres,
          joursPlanifies: joursPlanifies
        },
        ecart: joursEstimes - previousEstimation.current.jours,
        nouveauxJoursSuggeres: Math.ceil(joursEstimes)
      });
      
      setShowAjustementDialog(true);
      
      // Mettre à jour la référence
      previousEstimation.current = {
        jours: joursEstimes,
        nbMembres: nbMembres,
        joursPlanifies: joursPlanifies
      };
    }
  }, [
    form.chef_equipe_id, 
    form.dates_chantier, 
    form.montant_total_facture, 
    form.montant_soumission, 
    employees, 
    calculerHeuresBudgetees, 
    calculerCoutEquipe
  ]); // ✅ FIX: Toutes les dépendances ajoutées

  const resetWatcher = () => {
    previousEstimation.current = null;
  };

  return {
    showAjustementDialog,
    setShowAjustementDialog,
    ajustementData,
    resetWatcher
  };
};
